'use client';

import { useTranslations } from 'next-intl';
import { analytics } from 'lib/analytics/analytics';
import {
  heroDiscountCodes,
  getHeroDiscountPercentage,
} from 'components/shared/Cart/utils';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { AE_EMIRATE_STORAGE_KEY } from './AeDeliveryEta/config';
import { parseAeEmirate, type AeEmirate } from './AeDeliveryEta/emirates';
import { AfibShopifyMap } from 'content/products/addons/afib';
import { CardioAdaptibilityShopifyMap } from 'content/products/addons/cardioAdaptibility';
import { ClueShopifyMap, isClueAvailable } from 'content/products/addons/clue';
import { RespiratoryShopifyMap } from 'content/products/addons/respiratory';
import {
  AirRingColors,
  EngravingPrice,
  MiscRingCheckoutTypes,
  getAvailableAirRingColors,
  isRingAirColorDisabled,
  isRingAirSkuDisabled,
  productMap_air_ring,
  RingChargerType,
  RingPriceData,
  RingSizes,
} from 'content/products/ring';
import { getTravelChargerPrice } from 'content/products/accessories';
import { isRingAirLeanBuyFlow } from 'content/products/ring/buyFlow';
import { UhxShopifyMap } from 'content/products/uhx';
import {
  ringReferralCodes,
  getCampaignPromoDiscountConfig,
} from 'content/referral';
import { getCampaignConfig } from 'content/campaigns';
import { campaignDiscountAmount } from 'lib/pricing/displayedPrice';
import { CurrencySymbol } from 'components/shared/CurrencySymbol';
import { useForwardSearchParamsShopify } from 'hooks/navigation/useForwardSearchParamsShopify';
import {
  ShopifyStore,
  getEffectiveStoreForDiscount,
} from 'lib/commerce/shopify/shopifyHelper';
import { useCartStore, useShallow } from 'components/shared/Cart';
import {
  addonsReducer,
  getCheckoutLineItems,
  getShippingEstimate,
  initialAddonsState,
  initialRingSelectionContext,
} from './utils/checkout';
import { highlightElement } from './utils/highlightElement';
import {
  buildRingEcommerceItems,
  RING_BUY_GTM_EVENTS,
  sizeMethodOf,
  trackRingBuyAddToCart,
  trackRingBuyAddToCartFailed,
  trackRingBuyCtaClick,
  trackRingBuyOption,
  trackRingBuyViewItem,
  uhxPlanOf,
  type RingSelectionSnapshot,
} from './analytics/ringBuyEvents';
import { useRingBuySectionImpressions } from './analytics/useRingBuySectionImpressions';
import { getEngravingKey } from 'content/products/ring';
import { SizingKitShopifyMap } from 'content/products/ring';
import { useSddNddEligibility } from './DeliveryEtaBadge/useSddNddEligibility';
import {
  addonsType,
  CtaPlacement,
  RingContextType,
  ShippingEstimateObject,
  SizeDefaultState,
  UhxDefaultState,
} from './utils/types';
import { Currency } from 'utils/Currency';
import { CnoProShopifyMap } from 'content/products/addons/cnoPro';
import {
  RingStackType,
  StackVariant,
} from 'content/products/ring/stacking/constants';
import {
  getStackingPrice,
  getStackingDuoPrice,
} from 'content/products/ring/stacking/StackingShopifyMap';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { useIsCnOPlus } from 'hooks/pricing/useIsCnOPlus';
import {
  TradeInDevice,
  UHX_BONUS,
  getTradeInCredit,
  getMaxTradeInCredit,
} from 'content/products/tradeIn';

export const RingSelectionContext = createContext<RingContextType>(
  initialRingSelectionContext,
);

export const RingSelectionContextProvider: React.FC<{
  children?: React.ReactNode;
  currency: Currency;
  defaultColor: AirRingColors;
  defaultSize: RingSizes | null;
  defaultUhx: boolean;
  directCheckout?: boolean;
  misc?: MiscRingCheckoutTypes;
  estimates?: ShippingEstimateObject;
  notServicable?: boolean;
  notServicableMessage?: string;
  isOpenSizeDisabled?: boolean;
  showEngravingSectionProp?: boolean;
  onCheckoutStarted?: () => void;
}> = ({
  children,
  currency,
  defaultColor,
  defaultSize,
  defaultUhx,
  directCheckout,
  misc,
  estimates,
  notServicable,
  notServicableMessage,
  isOpenSizeDisabled,
  showEngravingSectionProp,
  onCheckoutStarted,
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const t = useTranslations('ring');
  const tCommon = useTranslations('common');
  const CartClient = useCartStore(
    useShallow((s) => ({
      workAffiliateDiscount: s.workAffiliateDiscount,
      store: s.store,
      addToCart: s.addToCart,
      affiliateDetails: s.affiliateDetails,
      getCampaignForProduct: s.getCampaignForProduct,
    })),
  );
  const {
    workAffiliateDiscount,
    store,
    addToCart,
    affiliateDetails,
    getCampaignForProduct,
  } = CartClient;
  const regionLocale = useRegionLocale();
  const country = searchParams?.get('locale') ?? regionLocale.region;
  const { isCnOPlus } = useIsCnOPlus();
  const isLeanBuyFlow = isRingAirLeanBuyFlow(store as ShopifyStore | null);
  // No UHX plan is pre-selected (IN's lean-flow 1-year default was removed).
  const defaultUhxCart: addonsType = null;

  const showSizeRef = useRef<HTMLDivElement>(null);
  const showChargerRef = useRef<HTMLDivElement>(null);
  const showTradeInRef = useRef<HTMLDivElement>(null);
  const tradeInOptionsRef = useRef<HTMLDivElement>(null);
  const engravingRef = useRef<HTMLDivElement>(null);
  const coverageRef = useRef<HTMLDivElement>(null);
  const powerplugRef = useRef<HTMLDivElement>(null);
  const cartSummaryRef = useRef<HTMLDivElement>(null);

  const sectionRefs = {
    showSizeRef,
    showChargerRef,
    showTradeInRef,
    tradeInOptionsRef,
    engravingRef,
    coverageRef,
    powerplugRef,
    cartSummaryRef,
  };

  // AE buy page: emirate chosen in the delivery ETA card. Null on the server
  // AND the first client paint (no hydration mismatch) — the per-tab
  // sessionStorage copy is restored in the effect below, after mount.
  const [selectedEmirate, setSelectedEmirateState] = useState<AeEmirate | null>(
    null,
  );
  // Same gate as every AE render site (BottomBar, ProductSelectionSection):
  // the URL region alone — NOT `country`, whose ?locale override would make
  // this restore disagree with the surfaces that display the value.
  const isAeRegion = regionLocale.region?.toUpperCase() === 'AE';
  useEffect(() => {
    if (!isAeRegion) return;
    try {
      const stored = parseAeEmirate(
        sessionStorage.getItem(AE_EMIRATE_STORAGE_KEY),
      );
      if (stored) setSelectedEmirateState(stored);
    } catch {
      // sessionStorage can throw in private modes — keep the null default.
    }
  }, [isAeRegion]);
  const setSelectedEmirate = useCallback((emirate: AeEmirate | null) => {
    setSelectedEmirateState(emirate);
    try {
      if (emirate === null) {
        sessionStorage.removeItem(AE_EMIRATE_STORAGE_KEY);
      } else {
        sessionStorage.setItem(AE_EMIRATE_STORAGE_KEY, emirate);
      }
    } catch {
      // Persistence is best-effort; in-memory state is the source of truth.
    }
  }, []);

  const ringCampaign = getCampaignForProduct('ring');
  const campaignConfig = getCampaignConfig(ringCampaign);

  const campaignActive = campaignConfig?.active ?? false;
  const colorParam = searchParams?.get('color');
  const availableColors = getAvailableAirRingColors(store);
  const resolvedDefaultColor = isRingAirColorDisabled(defaultColor, store)
    ? availableColors[0] ?? AirRingColors.BIONIC_GOLD
    : defaultColor;
  const [color, setColor] = useState<AirRingColors>(
    colorParam && availableColors.includes(colorParam as AirRingColors)
      ? (colorParam as AirRingColors)
      : resolvedDefaultColor,
  );
  const [size, setSize] = useState<RingSizes | null>(() => {
    const initial = isOpenSizeDisabled ? null : (defaultSize ?? RingSizes.OPEN);
    if (initial !== null && isRingAirSkuDisabled({ size: initial, store })) {
      return null;
    }
    return initial;
  });
  const [chargerType, setChargerType] = useState<RingChargerType>(
    RingChargerType.OG,
  );
  const [uhx, setUhx] = useState<boolean>(defaultUhx);
  // Analytics: has the buyer touched the pre-selected size / UHX plan?
  const [sizeTouched, setSizeTouched] = useState(false);
  const [uhxTouched, setUhxTouched] = useState(false);

  // DISABLED: Free 3-month UHX campaign ended - UHX section should now be visible for affiliates
  // useEffect(() => {
  //   if (affiliateDetails) setUhx(true);
  // }, [affiliateDetails]);

  const [showEngravingSection, setShowEngravingOption] = useState(
    () =>
      color !== AirRingColors.ROSE_GOLD && showEngravingSectionProp === false,
  );

  const [addonsState, dispatchAddons] = useReducer(addonsReducer, {
    ...initialAddonsState,
    uhxCart: defaultUhxCart,
  });
  const [isLoading, setIsLoading] = useState(false);

  const {
    engraving,
    afibCart,
    cardioAdaptibilityCart,
    cnoProCart,
    uhxCart,
    clueCart,
    respiratoryCart,
    tradeIn,
    stackType,
    stackVariant,
  } = addonsState;

  const setEngraving = (value: boolean) =>
    dispatchAddons({ type: 'SET_ENGRAVING', payload: value });
  const setEngravingMessage = (value: string | null) =>
    dispatchAddons({ type: 'SET_ENGRAVING_MESSAGE', payload: value });
  const setUhxCart = (value: addonsType) =>
    dispatchAddons({ type: 'SET_UHX_CART', payload: value });
  const setAfibCart = (value: addonsType) =>
    dispatchAddons({ type: 'SET_AFIB_CART', payload: value });
  const setCardioAdaptabilityCart = (value: addonsType) =>
    dispatchAddons({ type: 'SET_CARDIO_ADAPTIBILITY_CART', payload: value });
  const setCnoProCart = (value: addonsType) =>
    dispatchAddons({ type: 'SET_CNO_PRO_CART', payload: value });
  const setClueCart = (value: addonsType) =>
    dispatchAddons({ type: 'SET_CLUE_CART', payload: value });
  const setRespiratoryCart = (value: addonsType) =>
    dispatchAddons({ type: 'SET_RESPIRATORY_CART', payload: value });
  const setTradeInDevice = (device: TradeInDevice | null) =>
    dispatchAddons({ type: 'SET_TRADE_IN_DEVICE', payload: device });
  const setTradeInSerialNumber = (sn: string) =>
    dispatchAddons({ type: 'SET_TRADE_IN_SERIAL_NUMBER', payload: sn });
  const setStackType = (value: RingStackType | null) =>
    dispatchAddons({ type: 'SET_STACK_TYPE', payload: value });
  const setStackVariant = (value: StackVariant) =>
    dispatchAddons({ type: 'SET_STACK_VARIANT', payload: value });
  // UI setters mark the selection as touched; internal defaults/resets use the raw ones.
  const selectSize = (next: RingSizes) => {
    setSizeTouched(true);
    setSize(next);
  };
  const selectUhxCart = (value: addonsType) => {
    setUhxTouched(true);
    setUhxCart(value);
  };

  // Store can be seeded after first render; apply the UHX default once known.
  useEffect(() => {
    if (defaultUhxCart && uhxCart === null) setUhxCart(defaultUhxCart);
  }, [defaultUhxCart]);

  useEffect(() => {
    const isRoseGold = color === AirRingColors.ROSE_GOLD;
    dispatchAddons({
      type: 'SET_ROSE_GOLD_COLOR',
      payload: isRoseGold,
    });
    setShowEngravingOption(!isRoseGold);
    if (isRoseGold) {
      dispatchAddons({
        type: 'SET_ENGRAVING',
        payload: false,
      });
    }
  }, [color]);

  const forwardShopifySearchParams = useForwardSearchParamsShopify();
  const eSizingSize = Boolean(searchParams?.get('eSizingSize'));

  // Clue flow detection
  const isClueFlow =
    searchParams?.get('flow') === 'clue' &&
    isClueAvailable(store as ShopifyStore);

  // Respiratory flow detection
  const isRespiratoryFlow = searchParams?.get('flow') === 'respiratory';

  // Auto-enable clue addon when flow=clue
  useEffect(() => {
    if (isClueFlow && clueCart === null) {
      setClueCart('Year1');
    }
  }, [isClueFlow, clueCart]);

  // Auto-enable respiratory addon when flow=respiratory
  useEffect(() => {
    if (isRespiratoryFlow && respiratoryCart === null) {
      setRespiratoryCart('Year1');
    }
  }, [isRespiratoryFlow, respiratoryCart]);

  const requiredFields = [
    { ref: showSizeRef, condition: !size },
    { ref: showChargerRef, condition: !chargerType },

    { ref: engravingRef, condition: engraving === null },
    {
      ref: engravingRef,
      condition: engraving.enabled && !engraving.message,
    },
    { ref: coverageRef, condition: uhx === false && uhxCart === null },
  ];

  const goToNextStep = () => {
    analytics.track('Bottom bar continue - CLICK');
    for (const { ref, condition } of requiredFields) {
      if (condition) {
        // Lean flow: the column is short, so stop with the section on the
        // bottom bar instead of scrolling the images away.
        highlightElement(ref, { alignToBottomBar: isLeanBuyFlow });
        break;
      }
    }
  };

  const shippingEstimate = useMemo(
    () =>
      getShippingEstimate({ color, size, estimates, addonsState, t, tCommon }),
    [color, size, estimates, addonsState, t, tCommon],
  );

  const sddNdd = useSddNddEligibility({
    engravingSelected: engraving.enabled,
    isOpenSize: size === RingSizes.OPEN,
  });

  const referral = searchParams?.get('referral')
    ? ringReferralCodes[store as ShopifyStore]
    : undefined;

  const workAffiliate =
    searchParams?.get('workAffiliate') && searchParams?.get('discount');
  const currencySymbol = CurrencySymbol[currency.toLowerCase() as Currency];

  const ultraHumanPrice = useMemo(
    () => (uhxCart && !uhx && store ? UhxShopifyMap[store][uhxCart].price : 0),
    [uhxCart, uhx, store],
  );
  const ringPriceObject =
    store && color
      ? RingPriceData[store as ShopifyStore][color as AirRingColors]
      : { price: 0, striked_price: 0 };

  const engravingPrice = store && engraving.enabled ? EngravingPrice[store] : 0;
  const priceDrop = ringPriceObject.striked_price - ringPriceObject.price;
  const price = ringPriceObject.price + engravingPrice;

  const chargerPrice = useMemo(
    () =>
      store && chargerType === RingChargerType.TRAVEL_CHARGER
        ? getTravelChargerPrice(store) ?? 0
        : 0,
    [store, chargerType],
  );

  const afibPrice = useMemo(
    () =>
      afibCart ? AfibShopifyMap[store as ShopifyStore][afibCart].price : 0,
    [afibCart, store],
  );
  const cardioAdaptibilityPrice = useMemo(
    () =>
      cardioAdaptibilityCart
        ? CardioAdaptibilityShopifyMap[store as ShopifyStore][
            cardioAdaptibilityCart
          ].price
        : 0,
    [cardioAdaptibilityCart, store],
  );
  const cnoProPrice = useMemo(() => {
    if (!cnoProCart || !store) return 0;
    // Prices are consistent across Pro and Plus, always use Pro map for pricing
    return CnoProShopifyMap[store as ShopifyStore][cnoProCart].price;
  }, [cnoProCart, store]);

  const cluePrice = useMemo(() => {
    if (!clueCart) return 0;
    // When clue flow is active, use discounted price; otherwise use regular price
    if (isClueFlow) {
      return ClueShopifyMap[store as ShopifyStore]?.Year1?.discountedPrice || 0;
    }
    return ClueShopifyMap[store as ShopifyStore]?.Year1?.price || 0;
  }, [clueCart, store, isClueFlow]);

  const respiratoryPrice = useMemo(() => {
    if (!respiratoryCart) return 0;
    // When respiratory flow is active, use discounted price; otherwise use regular price
    if (isRespiratoryFlow) {
      return (
        RespiratoryShopifyMap[store as ShopifyStore]?.Year1?.discountedPrice ||
        0
      );
    }
    return RespiratoryShopifyMap[store as ShopifyStore]?.Year1?.price || 0;
  }, [respiratoryCart, store, isRespiratoryFlow]);

  const hasUhx = Boolean(uhx || uhxCart);
  const tradeInCredit = useMemo(() => {
    if (!tradeIn.device || !store) return 0;
    return getTradeInCredit(tradeIn.device, store, hasUhx);
  }, [tradeIn.device, store, hasUhx]);

  const tradeInBonus = useMemo(() => {
    if (!tradeIn.device || !store) return 0;
    return UHX_BONUS[store] ?? 0;
  }, [tradeIn.device, store]);

  const tradeInMaxCredit = useMemo(() => {
    if (!store) return 0;
    return getMaxTradeInCredit(store, true);
  }, [store]);

  const stackingPrice = useMemo(
    () =>
      store && stackVariant !== StackVariant.DUO
        ? getStackingPrice(store, stackType)
        : 0,
    [store, stackType, stackVariant],
  );

  const duoStackingPrice = useMemo(
    () =>
      store && stackVariant === StackVariant.DUO
        ? getStackingDuoPrice(store, stackType)
        : 0,
    [store, stackType, stackVariant],
  );

  // const isAffiliateLinkedInUS =
  //   store === ShopifyStore.US && affiliateDetails?.code === 'linkedinwellness';

  const affiliateDiscount = useMemo(() => {
    // Exclude affiliate discount when affiliateCode=birthday
    if (searchParams?.get('affiliateCode') === 'birthday') {
      return 0;
    }

    if (workAffiliate) {
      return (price * (workAffiliateDiscount?.discount ?? 0)) / 100;
    }
    const effectiveStore = getEffectiveStoreForDiscount(store);
    if (!effectiveStore) return 0;
    if (
      // (isRoseGold && !isAffiliateLinkedInUS) ||
      !affiliateDetails ||
      !affiliateDetails.ringActive ||
      !affiliateDetails.discount_configs[effectiveStore]?.RING?.code ||
      searchParams?.get('referral')
    )
      return 0;
    const discountConfig =
      affiliateDetails.discount_configs[effectiveStore]?.RING;
    return discountConfig?.type === 'percentage'
      ? (discountConfig.value * price) / 100
      : (discountConfig?.value ?? 0);
  }, [
    affiliateDetails,
    price,
    workAffiliate,
    workAffiliateDiscount,
    store,
    searchParams,
  ]);

  // --- Hero Discount ---
  const heroCategoryParam = searchParams?.get('heroCategory');
  const heroCategory =
    heroCategoryParam && heroDiscountCodes[heroCategoryParam]
      ? heroCategoryParam
      : null;
  const heroDiscount = heroCategory ? price * getHeroDiscountPercentage() : 0;

  // --- Birthday Discount ---
  // Ring uses only 'discount' parameter (Shopify sync)
  const birthdayDiscountCode = searchParams?.get('discount') as string;
  const affiliateCode = searchParams?.get('affiliateCode') as string;

  // Extract percentage from last 2 digits if they're numbers, otherwise default to 15%
  let birthdayDiscountPercentage = 0; // default
  if (birthdayDiscountCode && affiliateCode === 'birthday') {
    const lastTwoChars = birthdayDiscountCode.slice(-2);
    const parsedPercentage = parseInt(lastTwoChars);
    if (
      !isNaN(parsedPercentage) &&
      parsedPercentage > 0 &&
      parsedPercentage <= 100
    ) {
      birthdayDiscountPercentage = parsedPercentage;
    } else {
      birthdayDiscountPercentage = 15; // fallback to 15%
    }
  }
  const birthdayDiscount =
    birthdayDiscountCode &&
    birthdayDiscountCode.length > 0 &&
    affiliateCode === 'birthday'
      ? price * (birthdayDiscountPercentage / 100)
      : 0;

  // --- Ring Promo Discount ---
  const ringPromoDiscountAmount = useMemo(() => {
    return campaignDiscountAmount({
      product: 'ring',
      listPrice: price,
      campaigns: ringCampaign ? [ringCampaign] : [],
      fallback: getCampaignPromoDiscountConfig({
        store,
        country,
        product: 'ring',
      }),
    });
  }, [store, country, price, ringCampaign]);

  const discounts = useMemo(
    () => [
      { type: 'referral', amount: referral ? price * 0.1 : 0 },
      { type: 'affiliate', amount: affiliateDiscount },
      { type: 'hero', amount: heroDiscount },
      { type: 'birthday', amount: birthdayDiscount },
      { type: 'ringPromo', amount: ringPromoDiscountAmount },
    ],
    [
      referral,
      affiliateDiscount,
      heroDiscount,
      birthdayDiscount,
      ringPromoDiscountAmount,
      price,
    ],
  );

  // CONFIG: uncomment bellow & comment above for disabling the discount.
  // const discounts = useMemo(
  //   () => [
  //     { type: 'referral', amount: 0 },
  //     { type: 'affiliate', amount: 0 },
  //     { type: 'hero', amount: 0 },
  //   ],
  //   [],
  // );

  const effectiveDiscount = useMemo(() => {
    if (tradeIn.device)
      return {
        type: 'tradeIn' as const,
        amount: 0,
      };
    return discounts.reduce(
      (max, current) => (current.amount > max.amount ? current : max),
      { type: 'referral', amount: 0 },
    );
  }, [discounts, tradeIn.device]);

  const effectivePrice = useMemo(
    () =>
      price +
      chargerPrice +
      cardioAdaptibilityPrice +
      afibPrice +
      cnoProPrice +
      cluePrice +
      respiratoryPrice +
      stackingPrice +
      duoStackingPrice +
      ultraHumanPrice -
      effectiveDiscount.amount,
    [
      price,
      chargerPrice,
      cardioAdaptibilityPrice,
      afibPrice,
      cnoProPrice,
      cluePrice,
      respiratoryPrice,
      stackingPrice,
      duoStackingPrice,
      ultraHumanPrice,
      effectiveDiscount,
    ],
  );

  // useEffect(() => {
  //   if (!color || !size) return;
  //   if (affiliateDiscount || referral) {
  //     setPrice(
  //       RingPriceStriked[getEngravingKey(engraving.enabled)][
  //         store as ShopifyStore
  //       ],
  //     );
  //     return;
  //   }
  //   setPrice(
  //     RingPrice[getEngravingKey(engraving.enabled)][
  //       store as ShopifyStore
  //     ],
  //   );
  // }, [
  //   engraving.enabled,
  //   affiliateDiscount,
  //   store,
  //   color,
  //   size,
  //   referral,
  // ]);

  useEffect(() => {
    if (searchParams?.get('affiliate') || searchParams?.get('affiliateCode')) {
      setUhxCart(false);
    }
  }, [searchParams]);

  // --- Set roseGoldColor in addonsState when color changes ---

  const getExpressCheckoutLineItems = () => {
    if (!color || !size || !store || notServicable === true) return [];
    return getCheckoutLineItems({
      color,
      size,
      store,
      chargerType,
      engraving: engraving.enabled,
      engravingMessage: engraving.message,
      eSizingSize,
      uhxCart,
      afibCart,
      cardioAdaptibilityCart,
      cnoProCart,
      clueCart,
      respiratoryCart,
      stackType,
      stackVariant,
      misc,
      isCnOPlus,
      affiliateDetails,
      tradeInDevice: tradeIn.device,
      tradeInSerialNumber: tradeIn.serialNumber,
      tradeInCredit,
    });
  };

  // --- Analytics: selection snapshot, defaults, impressions ---
  const uhxDefaultKept = defaultUhxCart !== null && uhxCart === defaultUhxCart;
  const sizeDefaultKept = !isOpenSizeDisabled && size === RingSizes.OPEN;
  const uhxDefaultState: UhxDefaultState = !defaultUhxCart
    ? 'n/a'
    : uhxDefaultKept
      ? 'kept'
      : uhxCart === false
        ? 'declined'
        : 'changed';
  const sizeDefaultState: SizeDefaultState = isOpenSizeDisabled
    ? 'n/a'
    : sizeDefaultKept
      ? 'kept'
      : 'chosen';
  const mainVariantId =
    color && size
      ? productMap_air_ring[color]?.[size]?.[getEngravingKey(engraving.enabled)]
          ?.variantId[store as ShopifyStore]
      : undefined;
  const sizingKitVariantId = store
    ? (SizingKitShopifyMap[store as ShopifyStore]?.shopifyVariantId as
        | string
        | undefined)
    : undefined;
  const buildSnapshot = (): RingSelectionSnapshot => ({
    store: (store as ShopifyStore) ?? null,
    currency: String(currency).toUpperCase(),
    color,
    size,
    sizeMethod: sizeMethodOf(size),
    sizeDefaultKept,
    sizeTouched,
    uhxPlan: uhxPlanOf(uhxCart),
    uhxDefaultKept,
    uhxTouched,
    tradeIn: Boolean(tradeIn.device),
    tradeInDevice: tradeIn.device,
    tradeInCredit,
    variantId: mainVariantId ? String(mainVariantId) : undefined,
    price,
    effectivePrice,
    leanFlow: isLeanBuyFlow,
  });

  const viewItemTrackedRef = useRef(false);
  useEffect(() => {
    if (viewItemTrackedRef.current || !store || !color || !size || !price) {
      return;
    }
    viewItemTrackedRef.current = true;
    const snapshot = buildSnapshot();
    trackRingBuyViewItem(
      snapshot,
      buildRingEcommerceItems(snapshot, sizingKitVariantId),
    );
    trackRingBuyOption(RING_BUY_GTM_EVENTS.DEFAULTS_APPLIED, {
      sizeDefault: size === RingSizes.OPEN ? 'sizing_kit' : 'numeric',
      uhxDefault: defaultUhxCart ?? 'none',
      uhxForcedOff: Boolean(
        searchParams?.get('affiliate') || searchParams?.get('affiliateCode'),
      ),
      uhxHidden: defaultUhx,
      leanFlow: isLeanBuyFlow,
      store: String(store),
    });
    // snapshot is read once, when the selection first becomes priceable
  }, [store, color, size, price]);

  useRingBuySectionImpressions(
    [
      { section: 'size', ref: showSizeRef },
      { section: 'trade-in', ref: showTradeInRef },
      { section: 'uhx', ref: coverageRef },
      { section: 'order-summary', ref: cartSummaryRef },
    ],
    `${String(color)}-${String(size)}-${String(uhxCart)}`,
  );

  const checkout = async (placement: CtaPlacement = 'summary') => {
    onCheckoutStarted?.();
    const snapshot = buildSnapshot();
    trackRingBuyCtaClick(snapshot, 'add_to_cart', placement);
    if (!color || !size || !store || notServicable === true) {
      trackRingBuyAddToCartFailed(
        snapshot,
        'add_to_cart',
        placement,
        'empty_items',
      );
      return;
    }
    if (directCheckout) {
      if (
        misc &&
        ![null, undefined, ''].includes(
          productMap_air_ring[color]?.[size]?.NOT_ENGRAVED.misc?.[misc],
        )
      ) {
        const url =
          productMap_air_ring[color]?.[size]?.NOT_ENGRAVED.misc?.[misc];
        const route = forwardShopifySearchParams(url as string, 'ring_air');
        router.push(route);
        return;
      } else if (misc) {
        console.error('addToCart failed');
        return;
      }
      if (
        productMap_air_ring[color]?.[size]?.NOT_ENGRAVED.variantId[
          store as ShopifyStore
        ]
      ) {
        const product =
          productMap_air_ring[color]?.[size]?.NOT_ENGRAVED.variantId[
            store as ShopifyStore
          ];
        const route = forwardShopifySearchParams(product as string, 'ring_air');
        router.push(route);
      } else {
        console.error('addToCart failed');
      }
      return;
    }
    try {
      setIsLoading(true);
      const lineItemsArray = getCheckoutLineItems({
        color,
        size,
        store,
        chargerType,
        engraving: engraving.enabled,
        engravingMessage: engraving.message,
        eSizingSize,
        uhxCart: uhxCart,
        afibCart: afibCart,
        cardioAdaptibilityCart: cardioAdaptibilityCart,
        cnoProCart: cnoProCart,
        clueCart: clueCart,
        respiratoryCart: respiratoryCart,
        stackType,
        stackVariant,
        misc,
        isCnOPlus,
        affiliateDetails,
        tradeInDevice: tradeIn.device,
        tradeInSerialNumber: tradeIn.serialNumber,
        tradeInCredit,
        ctaPlacement: placement,
        uhxDefault: uhxDefaultState,
        sizeDefault: sizeDefaultState,
      });
      if (!lineItemsArray.length) {
        trackRingBuyAddToCartFailed(
          snapshot,
          'add_to_cart',
          placement,
          'empty_items',
        );
        return;
      }
      const added = await addToCart(lineItemsArray, !isLeanBuyFlow);
      if (!added) {
        // Covers policy-blocked adds and cart API errors (the store already toasts).
        trackRingBuyAddToCartFailed(
          snapshot,
          'add_to_cart',
          placement,
          'api_error',
        );
        return;
      }
      trackRingBuyAddToCart(
        snapshot,
        buildRingEcommerceItems(snapshot, sizingKitVariantId),
        'add_to_cart',
        placement,
      );
      // Reset selections to defaults (except color)
      setSize(isOpenSizeDisabled ? null : (defaultSize ?? RingSizes.OPEN));
      setChargerType(RingChargerType.OG);
      setUhx(defaultUhx);
      dispatchAddons({ type: 'RESET_ALL' });
      if (defaultUhxCart) setUhxCart(defaultUhxCart);
      setSizeTouched(false);
      setUhxTouched(false);
    } catch (error) {
      trackRingBuyAddToCartFailed(
        snapshot,
        'add_to_cart',
        placement,
        'exception',
        String(error).slice(0, 160),
      );
      alert('something went wrong!');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Generate JSON-LD schemas for Ring buy pages
  return (
    <RingSelectionContext.Provider
      value={{
        color,
        setColor,
        size,
        setSize: selectSize,
        chargerType,
        setChargerType,
        uhx,
        setUhx,
        uhxCart: uhxCart,
        setUhxCart: selectUhxCart,
        afibCart: afibCart,
        setAfibCart,
        cardioAdaptibilityCart: cardioAdaptibilityCart,
        setCardioAdaptabilityCart,
        cnoProCart: cnoProCart,
        setCnoProCart,
        clueCart: clueCart,
        setClueCart,
        respiratoryCart: respiratoryCart,
        setRespiratoryCart,
        tradeInDevice: tradeIn.device,
        setTradeInDevice,
        tradeInSerialNumber: tradeIn.serialNumber,
        setTradeInSerialNumber,
        tradeInCredit,
        tradeInBonus,
        tradeInMaxCredit,
        hasUhx,
        stackType,
        setStackType,
        stackVariant,
        setStackVariant,
        engraving: engraving.enabled,
        setEngraving,
        engravingMessage: engraving.message,
        setEngravingMessage,
        checkout,
        getExpressCheckoutLineItems,
        isLoading,
        defaultCurrency: currency,
        currencySymbol,
        store,
        price,
        ultraHumanPrice,
        effectivePrice,
        chargerPrice,
        shippingEstimate,
        selectedEmirate,
        setSelectedEmirate,
        sddNdd,
        affiliateDetails,
        campaignActive,
        affiliateDiscount,
        referral,
        sectionRefs,
        goToNextStep,
        effectiveDiscount,
        showEngravingSection,
        priceDrop,
        notServicable,
        notServicableMessage,
        isLeanBuyFlow,
        isOpenSizeDisabled,
        sizeDefaultKept,
        uhxDefaultKept,
      }}
    >
      {children}
    </RingSelectionContext.Provider>
  );
};
